import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinnerThird,
  faTriangleExclamation,
} from '@fortawesome/pro-light-svg-icons';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactHighlightedTextOverlay from 'shared/design-system/components/TrueImpactHighlightedTextOverlay';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { ProgramNameDonorFlag } from './useProgramNameDonorFlag';

export const DONOR_NAME_PROBLEM_TEXT = 'Donor name detected. See more';
export const DONOR_NAME_PRIVATE_TEXT = 'This report will be set as private.';

interface ProgramNameDonorFieldProps {
  value: string;
  donor: ProgramNameDonorFlag;
  onChange: (value: string) => unknown;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
}

const FieldWrapper = styled.div`
  position: relative;
  width: 100%;
  input {
    font-family: inherit;
    padding-right: 44px;
  }
  .donor-name-spinner {
    position: absolute;
    top: 50%;
    right: ${SPACERS.medium};
    transform: translateY(-50%);
    z-index: 2;
    pointer-events: none;
    color: ${COLORS.grayBase};
  }
  .donor-name-overlay {
    box-sizing: border-box;
    padding: 14px;
    border: 1px solid transparent;
    font-family: inherit;
    font-size: 16px;
    line-height: normal;
    white-space: pre;
  }
`;

const ProblemTag = styled.button<{ kept: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACERS.extraTiny};
  margin-top: ${SPACERS.extraTiny};
  padding: ${SPACERS.extraTiny} ${SPACERS.small};
  border-radius: 999px;
  cursor: pointer;
  font-size: 12px;
  line-height: 1.4;
  background: ${COLORS.whiteBase};
  color: ${({ kept }) => (kept ? COLORS.warningDark : COLORS.critical)};
  border: 1px solid
    ${({ kept }) => (kept ? COLORS.warning : COLORS.critical)};
  ${({ kept }) => (kept ? `background: ${COLORS.warningBackground};` : '')}
`;

const ProgramNameDonorField: React.FC<ProgramNameDonorFieldProps> = ({
  value,
  donor,
  onChange,
  placeholder = 'Program Name',
  autoFocus,
  disabled,
  className,
}) => (
  <div className={className}>
    <FieldWrapper>
      <TrueImpactTextfield
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        onChange={onChange}
      />
      <TrueImpactHighlightedTextOverlay
        text={value}
        ranges={donor.ranges}
        className="donor-name-overlay"
      />
      {donor.isChecking && (
        <FontAwesomeIcon
          icon={faSpinnerThird}
          spin
          className="donor-name-spinner"
          aria-label="Validating program name"
        />
      )}
    </FieldWrapper>
    {donor.detected && (
      <ProblemTag type="button" kept={donor.kept} onClick={donor.openModal}>
        <FontAwesomeIcon icon={faTriangleExclamation} />
        {donor.kept ? DONOR_NAME_PRIVATE_TEXT : DONOR_NAME_PROBLEM_TEXT}
      </ProblemTag>
    )}
  </div>
);

export default ProgramNameDonorField;
